import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import API from "../api/axios";
import { useAuth } from "../context/AuthContext";
import {
  MdAdd, MdEdit, MdDelete,
  MdCircle, MdSearch, MdRouter,
} from "react-icons/md";

const deviceTypes = ["router", "switch", "server", "access_point", "firewall"];

const Devices = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "NETWORK_ADMINISTRATOR";

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const [formData, setFormData] = useState({
    name: "", ipAddress: "", hostname: "", type: "router",
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const res = await API.get("/devices");
      setDevices(res.data);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditDevice(null);
    setFormData({ name: "", ipAddress: "", hostname: "", type: "router" });
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (device) => {
    setEditDevice(device);
    setFormData({
      name: device.name,
      ipAddress: device.ipAddress,
      hostname: device.hostname || "",
      type: device.type,
    });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditDevice(null);
    setFormError("");
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.ipAddress) {
      setFormError("Name and IP Address are required");
      return;
    }

    setSubmitting(true);
    setFormError("");

    try {
      if (editDevice) {
        await API.put(`/devices/${editDevice.id}`, formData);
      } else {
        await API.post("/devices", formData);
      }
      await fetchDevices();
      closeModal();
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to save device");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this device?")) return;
    try {
      await API.delete(`/devices/${id}`);
      await fetchDevices();
    } catch {
      alert("Failed to delete device");
    }
  };

  const filtered = devices.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.ipAddress.includes(search)
  );

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Devices</h2>
          <p className="text-sm text-gray-500">
            Manage and monitor registered network devices
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
          >
            <MdAdd size={18} />
            Add Device
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="relative">
          <MdSearch
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search by name or IP address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Devices Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading devices...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <MdRouter size={40} className="mx-auto mb-2 text-gray-300" />
            <p>No devices found</p>
            {isAdmin && (
              <button
                onClick={openAddModal}
                className="mt-3 text-blue-600 hover:underline text-sm"
              >
                Add your first device
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-6 py-3 text-left">Device</th>
                <th className="px-6 py-3 text-left">IP Address</th>
                <th className="px-6 py-3 text-left">Hostname</th>
                <th className="px-6 py-3 text-left">Type</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Latency</th>
                <th className="px-6 py-3 text-left">Last Seen</th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((device) => (
                <tr key={device.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">
                    {device.name}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {device.ipAddress}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {device.hostname || "—"}
                  </td>
                  <td className="px-6 py-4 text-gray-600 capitalize">
                    {device.type.replace("_", " ")}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`flex items-center gap-1 text-xs font-medium ${
                        device.status === "ONLINE"
                          ? "text-green-600"
                          : device.status === "OFFLINE"
                          ? "text-red-600"
                          : "text-gray-500"
                      }`}
                    >
                      <MdCircle size={10} />
                      {device.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {device.latency ? `${device.latency}ms` : "—"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {device.lastSeen
                      ? new Date(device.lastSeen).toLocaleString()
                      : "—"}
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(device)}
                          className="text-blue-500 hover:text-blue-700 transition"
                        >
                          <MdEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(device.id)}
                          className="text-red-500 hover:text-red-700 transition"
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {editDevice ? "Edit Device" : "Register New Device"}
            </h3>

            {formError && (
              <div className="bg-red-100 text-red-600 px-4 py-2 rounded mb-4 text-sm">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Router A"
                />
              </div>

              {/* IP Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IP Address *
                </label>
                <input
                  type="text"
                  name="ipAddress"
                  value={formData.ipAddress}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 192.168.1.1"
                />
              </div>

              {/* Hostname */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hostname
                </label>
                <input
                  type="text"
                  name="hostname"
                  value={formData.hostname}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. router-a.local"
                />
              </div>

              {/* Device Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {deviceTypes.map((t) => (
                    <option key={t} value={t}>
                      {t.replace("_", " ").toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={closeModal}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-sm"
              >
                {submitting
                  ? "Saving..."
                  : editDevice
                  ? "Update Device"
                  : "Register Device"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Devices;